import { Component, inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { LearningSectionJsonModel } from '../learning-section.json.model';
import { MatButton } from '@angular/material/button';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { Store } from '@ngxs/store';
import { AsyncPipe } from '@angular/common';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { LearningSectionLayoutState } from '../ngxs/learning-section-layout/learning-section-layout.state';
import { LearningSectionLayoutActions } from '../ngxs/learning-section-layout/learning-section-layout.actions';
import { MatDividerModule } from '@angular/material/divider';
import { AppSelectors } from '../../app.state';
import { ConsumerJsonModel } from '../../consumer/consumer.json.model';
import { SubjectJsonModel } from '../../subject/subject.json.model';
import { MatSlideToggle } from '@angular/material/slide-toggle';

@Component({
  selector: 'pixl-learning-section-edit',
  imports: [
    MatDialogTitle,
    FormsModule,
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    AsyncPipe,
    MatOption,
    MatSelect,
    MatDividerModule,
    MatSlideToggle
  ],
  templateUrl: './learning-section-edit.component.html',
  styleUrl: './learning-section-edit.component.scss'
})
export class LearningSectionEditComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<LearningSectionEditComponent>);
  private fb = inject(FormBuilder);

  private store = inject(Store);
  private appState$ = this.store.select(AppSelectors.appState);

  public consumers: ConsumerJsonModel[] = [];
  public subjects: SubjectJsonModel[] = [];
  public layouts$ = this.store.select(LearningSectionLayoutState.getLearningSectionLayouts);

  public data = inject<LearningSectionJsonModel>(MAT_DIALOG_DATA);

  public formGroup = this.fb.group({
    name: [this.data.name, Validators.required],
    title: [this.data.title, Validators.required],
    learningSectionType: [this.data.learningSectionType, Validators.required],
    showCopyright: [this.data.showCopyright],
    consumer: this.data.consumer || null,
    subjects: [this.data.subjects || []],
    learningSectionLayout: [this.data.learningSectionLayout, [Validators.required]]
  });

  ngOnInit(): void {
    this.appState$.subscribe(({ isLoaded, consumers, subjects }) => {
      if (isLoaded) {
        this.consumers = consumers;
        this.subjects = subjects;
      }
    });

    this.store.dispatch([
      new LearningSectionLayoutActions.Fetch({
        pageNumber: 1,
        pageSize: -1,
        skipStoreDispatch: true
      })
    ]);
  }

  public onSave(): void {
    if (this.formGroup.valid) {
      const formData = this.formGroup.value;
      if (this.data.hasId()) {
        this.data.update(formData);
        this.dialogRef.close(this.data);
      }
      else {
        /**
         * `this.data.update(...)` fails due to a missing collection for the model.
         * Collections are part of the
         * [DATX API](https://datx.dev/docs/api-reference/collection) for their data store.
         *
         * We do not use the data store, as we have opted for NGXS as our state manager.
         * Data is stored in the backend application and fetchted continously.
         *
         * Deep merging the learning section is a workaround to mitigate the collection
         * relationship error. It ought to be investigated if using datx datastore
         * fixes this problem and whether the frontend application should use it as
         * its data layer
         */
        const section = {
          ...this.data,
          ...formData,
          subjects: [...this.data.subjects, ...(formData.subjects ? formData.subjects : [])]
        };
        this.dialogRef.close(section);
      }
    }
  }

  public checkForErrors(formControl: AbstractControl): string {
    if (formControl.hasError('required')) {
      return 'Eingabe erforderlich';
    }

    return '';
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
